import React, { useState, useEffect, useCallback, useMemo } from "react"
import PropTypes from "prop-types"
import { connect } from "react-redux"
import { Spinner } from "reactstrap"
import { setBreadcrumbItems } from "../../../store/actions"
import { descargarPerfilExport } from "./api"
import { rangoDelMes } from "./helpers"
import HeaderFiltros from "./HeaderFiltros"
import ChipsSegmentos from "./ChipsSegmentos"
import TablaCatalogo from "./TablaCatalogo"
import DrawerPerfil360 from "./DrawerPerfil360"

const breadcrumbItems = [
  { title: "LS-DA", link: "#" },
  { title: "Mercadeo", link: "#" },
  { title: "Perfil de Cliente 360°", link: "#" },
]

const ahora = new Date()

const PerfilCliente360 = ({ empresaSeleccionada, user, setBreadcrumbItems: setBreadcrumb }) => {
  const [modo, setModo] = useState("mes")
  const [year, setYear] = useState(ahora.getFullYear())
  const [month, setMonth] = useState(ahora.getMonth() + 1)
  const [fechaInicio, setFechaInicio] = useState("")
  const [fechaFin, setFechaFin] = useState("")
  const [campoFecha, setCampoFecha] = useState("fecha")
  const [sucursal, setSucursal] = useState("")
  const [segmento, setSegmento] = useState("")
  const [excluirCF, setExcluirCF] = useState(true)
  const [orderBy, setOrderBy] = useState("monetary")
  const [orderDir, setOrderDir] = useState("desc")

  const [filtrosAplicados, setFiltrosAplicados] = useState(null)
  const [metadata, setMetadata] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState("")

  const [exportando, setExportando] = useState(null)
  const [exportInfo, setExportInfo] = useState(null)

  const [drawerAbierto, setDrawerAbierto] = useState(false)
  const [clienteActivo, setClienteActivo] = useState(null)

  // refreshKey para forzar reload del catálogo desde el padre (cuando cambian filtros aplicados)
  const [refreshKey, setRefreshKey] = useState(0)

  const nitemisor = empresaSeleccionada?.nit || empresaSeleccionada?.identificacion || ""
  const empresaNombre = empresaSeleccionada?.nombre || ""

  useEffect(() => {
    document.title = "Perfil de Cliente 360° | LS-DA"
    const greeting = user
      ? `Bienvenido, ${user.nombre || ""} ${user.apellido || ""}`.trim()
      : "Perfil de Cliente 360°"
    setBreadcrumb(greeting, breadcrumbItems)
  }, [user, setBreadcrumb])

  // Reset al cambiar empresa
  useEffect(() => {
    setFiltrosAplicados(null)
    setMetadata(null)
    setError("")
    setSegmento("")
    setSucursal("")
    setDrawerAbierto(false)
    setClienteActivo(null)
  }, [nitemisor])

  const construirFiltros = useCallback(() => {
    const base = {
      nitemisor,
      campo_fecha: campoFecha,
      sucursal: sucursal || undefined,
      excluir_consumidor_final: excluirCF,
    }
    if (modo === "mes") {
      const { fecha_inicio, fecha_fin } = rangoDelMes(year, month)
      return { ...base, fecha_inicio, fecha_fin }
    }
    return { ...base, fecha_inicio: fechaInicio, fecha_fin: fechaFin }
  }, [nitemisor, modo, year, month, fechaInicio, fechaFin, campoFecha, sucursal, excluirCF])

  const puedeConsultar = useMemo(() => {
    if (!nitemisor) return false
    if (modo === "mes") return Boolean(year && month)
    return Boolean(fechaInicio && fechaFin && fechaInicio <= fechaFin)
  }, [nitemisor, modo, year, month, fechaInicio, fechaFin])

  const handleConsultar = () => {
    setLoading(true)
    setError("")
    const opts = construirFiltros()
    setFiltrosAplicados(opts)
    setRefreshKey(k => k + 1)
    // El loading de la tabla se gestiona localmente, no bloqueamos aquí
    setTimeout(() => setLoading(false), 200)
  }

  const handleExportar = async (formato) => {
    if (!filtrosAplicados || exportando) return
    setExportando(formato)
    setExportInfo(null)
    setError("")
    try {
      const info = await descargarPerfilExport({ ...filtrosAplicados, segmento: segmento || undefined, formato })
      setExportInfo({ formato, totalRows: info.totalRows, filename: info.filename })
      setTimeout(() => setExportInfo(null), 6000)
    } catch (err) {
      setError(err.response?.data?.error || err.message || "Error al exportar.")
    } finally {
      setExportando(null)
    }
  }

  const handleAbrirPerfil = (cliente) => {
    setClienteActivo(cliente)
    setDrawerAbierto(true)
  }

  if (!nitemisor) {
    return (
      <div style={{ background: "#fff", borderRadius: 14, padding: 60, textAlign: "center", border: "1px dashed #cbd5e1" }}>
        <i className="mdi mdi-domain-off" style={{ fontSize: 56, color: "#cbd5e1" }} />
        <h5 className="mt-3" style={{ color: "#0f172a" }}>Selecciona una empresa</h5>
        <p style={{ color: "#64748b", maxWidth: 420, margin: "8px auto 0" }}>
          Usa el selector <strong>“Seleccionar empresa”</strong> en la parte superior derecha.
        </p>
      </div>
    )
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {/* Header */}
      <div style={{ background: "#fff", borderRadius: 14, padding: "20px 24px", border: "1px solid #e2e8f0" }}>
        <div className="d-flex justify-content-between align-items-start flex-wrap gap-3 mb-3">
          <div>
            <div style={{ fontSize: 12, color: "#64748b", textTransform: "uppercase", fontWeight: 600, letterSpacing: 0.4 }}>
              Mercadeo
            </div>
            <h3 style={{ margin: 0, fontSize: 24, fontWeight: 700, color: "#0f172a" }}>
              Perfil de Cliente 360°
            </h3>
            <div style={{ fontSize: 13, color: "#64748b", marginTop: 4 }}>
              <i className="mdi mdi-domain me-1 text-primary" />
              <strong>{empresaNombre}</strong> · NIT {nitemisor}
            </div>
          </div>
          {filtrosAplicados && (
            <div className="btn-group btn-group-sm" role="group">
              <button type="button" className="btn btn-outline-success"
                onClick={() => handleExportar("xlsx")} disabled={!!exportando}>
                {exportando === "xlsx" ? (<><Spinner size="sm" className="me-1" /> Generando…</>) : (<><i className="mdi mdi-file-excel me-1" /> Excel</>)}
              </button>
              <button type="button" className="btn btn-outline-primary"
                onClick={() => handleExportar("csv")} disabled={!!exportando}>
                {exportando === "csv" ? (<><Spinner size="sm" className="me-1" /> Generando…</>) : (<><i className="mdi mdi-file-delimited me-1" /> CSV</>)}
              </button>
            </div>
          )}
        </div>

        <HeaderFiltros
          modo={modo} setModo={setModo}
          year={year} setYear={setYear} month={month} setMonth={setMonth}
          fechaInicio={fechaInicio} setFechaInicio={setFechaInicio}
          fechaFin={fechaFin} setFechaFin={setFechaFin}
          campoFecha={campoFecha} setCampoFecha={setCampoFecha}
          sucursal={sucursal} setSucursal={setSucursal}
          sucursalesDisponibles={[]}
          segmento={segmento} setSegmento={setSegmento}
          excluirCF={excluirCF} setExcluirCF={setExcluirCF}
          orderBy={orderBy} setOrderBy={setOrderBy}
          orderDir={orderDir} setOrderDir={setOrderDir}
          loading={loading}
          onConsultar={handleConsultar}
          puedeConsultar={puedeConsultar}
        />

        {exportInfo && (
          <div style={{
            marginTop: 12,
            background: "#d1fae5", border: "1px solid #a7f3d0",
            color: "#065f46", padding: "10px 14px", borderRadius: 8,
            fontSize: 13, display: "flex", alignItems: "center", gap: 10,
          }}>
            <i className="mdi mdi-check-circle" style={{ fontSize: 18 }} />
            <span>
              Descarga lista{exportInfo.totalRows ? `: ${exportInfo.totalRows.toLocaleString("es-CO")} filas` : ""}
              {exportInfo.filename ? ` · ${exportInfo.filename}` : ""}
            </span>
            <button type="button" className="btn-close ms-auto" style={{ fontSize: 10 }} onClick={() => setExportInfo(null)} />
          </div>
        )}
      </div>

      {/* Error */}
      {error && (
        <div style={{
          padding: "14px 18px", background: "#fef2f2", border: "1px solid #fecaca",
          color: "#991b1b", borderRadius: 10,
          display: "flex", alignItems: "center", gap: 10,
        }}>
          <i className="mdi mdi-alert-circle" style={{ fontSize: 20 }} />
          <span>{error}</span>
        </div>
      )}

      {/* Empty state */}
      {!filtrosAplicados && (
        <div style={{ background: "#fff", borderRadius: 14, padding: "60px 40px", textAlign: "center", border: "1px dashed #cbd5e1" }}>
          <i className="mdi mdi-account-search-outline" style={{ fontSize: 56, color: "#cbd5e1" }} />
          <h5 className="mt-3" style={{ color: "#0f172a", fontWeight: 600 }}>
            Configura el período y consulta
          </h5>
          <p style={{ color: "#64748b", maxWidth: 460, margin: "8px auto 0", fontSize: 14 }}>
            Indica mes/año o rango de fechas y pulsa <strong>Consultar</strong> para ver el catálogo de clientes
            con segmentación RFM, scores y predicciones de <strong>{empresaNombre}</strong>.
          </p>
        </div>
      )}

      {/* Chips de segmentos + tabla */}
      {filtrosAplicados && (
        <>
          {metadata?.segmentos_count && (
            <ChipsSegmentos
              segmentosCount={metadata.segmentos_count}
              total={metadata.total_receptores_disponibles ?? metadata.total ?? 0}
              segmentoActivo={segmento}
              onClickSegmento={setSegmento}
            />
          )}

          <TablaCatalogo
            filtros={filtrosAplicados}
            segmento={segmento}
            orderBy={orderBy}
            orderDir={orderDir}
            onMetadataChange={setMetadata}
            onAbrirPerfil={handleAbrirPerfil}
            refreshKey={refreshKey}
          />
        </>
      )}

      {/* Drawer drill-down 360° */}
      <DrawerPerfil360
        abierto={drawerAbierto}
        nitReceptor={clienteActivo?.nit_receptor}
        clienteBase={clienteActivo}
        filtros={filtrosAplicados}
        onCerrar={() => setDrawerAbierto(false)}
      />
    </div>
  )
}

PerfilCliente360.propTypes = {
  empresaSeleccionada: PropTypes.object,
  user: PropTypes.object,
  setBreadcrumbItems: PropTypes.func.isRequired,
}

const mapStateToProps = state => ({
  empresaSeleccionada: state.Login.empresaSeleccionada,
  user: state.Login.user,
})

export default connect(mapStateToProps, { setBreadcrumbItems })(PerfilCliente360)
